import {defer, EMPTY, MonoTypeOperatorFunction, pipe} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {dependOn} from './depend-on';

/**
 * RxJS operator, that allows to handle the following events:
 *  - subscription to the observable
 *  - unsubscription from the observable
 *  - next
 *  - complete
 *  - error
 *
 * @example
 * observable$.pipe(
 *     hook({
 *        subscribe: () => console.warn('subscribe'),
 *        unsubscribe: () => console.warn('unsubscribe'),
 *        complete: () => console.warn('complete'),
 *        error: err => console.warn('error', err),
 *        next: v => console.warn('next', v)
 *    })
 * );
 *
 * @param {() => void} subscribe
 * @param {() => void} unsubscribe
 * @param {() => void} complete
 * @param {error => void} error
 * @param {(value: T) => void} next
 * @returns {MonoTypeOperatorFunction<T>}
 */
export const hook = <T>(
    {subscribe, unsubscribe, complete, error, next}: {
        subscribe?: () => void;
        unsubscribe?: () => void;
        complete?: () => void;
        error?: (error: any) => void;
        next?: (value: T) => void;
    }
): MonoTypeOperatorFunction<T> => {
    const operators: Array<MonoTypeOperatorFunction<T>> = [];

    if (subscribe) {
        operators.push(
            dependOn(defer(() => {
                subscribe();

                return EMPTY;
            }))
        );
    }

    if (unsubscribe) {
        operators.push(
            finalize(unsubscribe)
        );
    }

    if (complete || error || next) {
        operators.push(
            tap({
                complete,
                error,
                next
            })
        );
    }

    return (pipe as (...args: Array<MonoTypeOperatorFunction<T>>) => MonoTypeOperatorFunction<T>)(...operators);
};
